import React from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, Title, Tooltip, Legend, ScatterController } from 'chart.js';
import { Bar } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, Title, Tooltip, Legend, ScatterController, ChartDataLabels);

export default function BrandAttributesChart({ selfData, competitorData, loading }) {
    if (loading) {
        return (
          <div className="App h-w-100 white-color">
            <p className="text-black ms-4">Processing...</p>
          </div>
        );
      }
    const isSelfDataValid = selfData && selfData.length > 0;
    const isCompetitorDataValid = competitorData && competitorData.length > 0;
    if (!isSelfDataValid || !isCompetitorDataValid) {
        return <div className="App h-w-100 white-color">
            <p className="text-black ms-4">No data available!</p>
        </div>;
    }

    const formatValue = (value) => {
        if (typeof value === 'number' && !isNaN(value)) {
            return parseFloat(value.toFixed());
        }
        return 0; // Fallback to 0 if value is undefined or not a number
    };

    const formatLabel = (label) => {
        if (!label) return label;
        if (label === "Committed to agribusiness for the long term") {
            return ["Committed to agribusiness", "for the long term"];
        }
        if (label === "Has staff based in rural communities") {
            return ["Has staff based in rural", "communities"];
        }
        if (label === "Is a leader in supporting sustainable farming") {
            return ["Is a leader in supporting", "sustainable farming"];
        }
        if (label === "Main bank for borrowing") {
            return ["Main bank for", "borrowing"];
        }
        if (label === "Is the best bank for Agri lending") {
            return ["Is the best bank for Agri", "lending"];
        }
        return [label];
    };
    
    
    const formattedLabels = selfData
    ? selfData
          .filter(item => item.parameter !== "None of these")
          .map(item => formatLabel(item.parameter))
    : [];

    const formatRespondantCount = (value) => {
        if (typeof value === 'number' && !isNaN(value)) {
            return parseFloat(value.toFixed());
        }
        return 0; // Fallback to 0 if value is undefined or not a number
    };

    const data = {
        labels: formattedLabels,
        datasets: [
            {
                label: selfData ? selfData[0]['brand_name'] : '',
                data: selfData ? selfData.map(item => formatValue(item.value)) : [],
                backgroundColor: 'rgba(0, 0, 0, 0)',
                borderColor: '#f06b22',
                borderWidth: 3,
                pointRadius: window.innerWidth < 767 ? 13 : 18,
                pointHoverRadius: window.innerWidth < 767 ? 13 : 18,
                pointBackgroundColor: '#f06b22',
                type: 'scatter',
                borderRadius: 4,
                pointStyle: 'rectRounded',
                tension: 0.4,
                datalabels: {
                    align: 'center',
                    anchor: 'center',
                    color: '#fff',
                    font: {
                        size: window.innerWidth < 767 ? 10 : 12,
                        family: 'Graphik-Semibold, sans-serif',
                    },
                },
                respondentCounts: selfData ? selfData.map(item => formatValue(item.respondent_count)) : [],
            },
            {
                label: competitorData ? competitorData[0]['comp_name'] : '',
                data: competitorData ? competitorData.map(item => formatValue(item.value)) : [],
                backgroundColor: 'rgba(0, 0, 0, 0)',
                borderColor: '#dae8d8',
                borderWidth: 3,
                pointRadius: window.innerWidth < 767 ? 13 : 16, 
                pointHoverRadius: window.innerWidth < 767 ? 13 : 16,
                pointBackgroundColor: '#dae8d8',
                type: 'scatter',
                borderRadius: 4,
                pointStyle: 'rectRounded',
                datalabels: {
                    color: '#313932',
                    font: {
                        size: window.innerWidth < 767 ? 10 : 12,
                        family: 'Graphik-Regular, sans-serif',
                    },
                },
                respondentCounts: competitorData ? competitorData.map(item => formatValue(item.respondent_count)) : [],
            }
        ]
    };

    const splitTextIntoChunks = (text, chunkSize) => {
        const words = text ? text.split(' ') : '';
        const chunks = [];

        for (let i = 0; i < words.length; i += chunkSize) {
            chunks.push(words.slice(i, i + chunkSize).join(' '));
        }
        return chunks;
    };

    const dottedLinePlugin = {
        id: 'dottedLinePlugin',
        beforeDraw(chart) {
            const { ctx, scales: { y, x }, chartArea: { left, right } } = chart;
            const selfDataset = chart.data.datasets[0].data;
            const competitorDataset = chart.data.datasets[1].data; 
    
            ctx.save();
            
            ctx.strokeStyle = 'grey';
            ctx.lineWidth = 1.5;
            ctx.setLineDash([1, 1.5]);
    
            const yAxisTicks = y.getTicks();
            yAxisTicks.forEach((tick, index) => {
                const yPosition = y.getPixelForTick(index) - 1;
                if (yPosition !== undefined && !isNaN(yPosition)) {
                    ctx.beginPath();
                    ctx.moveTo(left, yPosition);
                    ctx.lineTo(right, yPosition);
                    ctx.stroke();
                }
            });
    
            ctx.setLineDash([]); 
            ctx.lineWidth = 3.5; 
            ctx.strokeStyle = '#dae8d8';
    
            selfDataset.forEach((selfValue, index) => {
                const competitorValue = competitorDataset[index];
                const yPosition = y.getPixelForTick(index) - 1; 
                if (yPosition !== undefined && !isNaN(yPosition)) {
                    if (selfValue !== undefined && competitorValue !== undefined) {
                        const selfX = x.getPixelForValue(selfValue);
                        const competitorX = x.getPixelForValue(competitorValue);
    
                        if (!isNaN(selfX) && !isNaN(competitorX)) {
                            ctx.beginPath();
                            ctx.moveTo(selfX, yPosition-1);
                            ctx.lineTo(competitorX, yPosition-1);
                            ctx.stroke();
                        }
                    }
                }
            });            
            ctx.restore();
        }
    };
    
    const options = {
        indexAxis: 'y', // Horizontal bar chart layout
        responsive: true,
        animation: true,
        maintainAspectRatio: window.innerWidth < 767 ? false : true,
        devicePixelRatio: window.devicePixelRatio || 1,
        scales: {
            x: {
                beginAtZero: true,
                max: 105,  
                grid: {
                    display: false // Remove grid lines
                },
                ticks: {
                    display: false,
                    color: 'white', // X-axis label color
                    font: {
                        family: 'Graphik-Light, sans-serif',
                    }
                },
                border: {
                    display: false, // Remove the x-axis line
                },
            },
            y: {
                grid: {
                    display: false, // Remove grid lines
                    offset: false,
                    color: function (context) {
                        return context.tick && context.tick.label ? '#4f5c50' : 'transparent'; // Show grid line only in the center of labels
                    },
                    lineWidth: 0.3,
                },
                ticks: {
                    color: '#313932', // Y-axis label color
                    align: 'center',
                    padding: window.innerWidth < 767 ? 6 : 12,
                    crossAlign: 'far', //align the labels to left                     
                    font: (context) => {
                        const width = context.chart.width;
                        return {
                            size: window.innerWidth < 1100 ? (window.innerWidth < 400 ? 9 : 12) : 14, // Responsive font size for Y-axis
                            family: 'Graphik-Light, sans-serif',
                            weight: '500',
                            color: '#313932',
                        };
                    },
                },
                border: {
                    display: false,
                },
                min: -2,
            }
        },
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                font: (context) => {
                    const width = context.chart.width;
                    return {
                        size: window.innerWidth < 1100 ? 10 : 15, // Responsive font size for Y-axis
                        family: 'Graphik-Light, sans-serif',
                        weight: '500',
                        color: '#313932',
                    };
                },
                formatter: (value) => value.toFixed() + '%',                
            },
            tooltip: {
                callbacks: {
                    title: function (tooltipItem) {
                        const label = tooltipItem[0].label || '';
                        const formattedLabel = String(label).replace(/,/g, ' ');

                        const dataset = tooltipItem[0].dataset;
                        const bandName = dataset.label || '';

                        // const firstLine = `Do you associate the following with ${bandName === 'Rabobank' ? 'this bank' : ''}`;
                        const firstLine = `Do you associate the following with this brand`;
                        const secondLine = `- '${formattedLabel}.'`;
                        // const additionalText = `Do you associate the following with ${bandName === 'Rabobank' ? 'this bank' : ''} - '${formattedLabel}.'`;
                        const additionalText = `Do you associate the following with this brand - '${formattedLabel}.'`;
                        return window.innerWidth < 700 ? [firstLine, secondLine] : additionalText;
                    },
                    label: function (tooltipItem) {

                        const dataset = tooltipItem.dataset;
                        const dataIndex = tooltipItem.dataIndex;
                        const value = tooltipItem.raw;
                        
                        const respondentCount = dataset.respondentCounts && dataset.respondentCounts[dataIndex] !== undefined
                                ? dataset.respondentCounts[dataIndex]
                                : 'N/A';
                            
                        return `${dataset.label}: ${value}% | Count: ${respondentCount}`;
                        // return `${tooltipItem.dataset.label}: ${tooltipItem.raw}%`;
                    }
                },
                titleFont: {
                    family: 'Graphik-Light, sans-serif',
                    size: 12, 
                },
                bodyFont: {
                    family: 'Graphik-Light, sans-serif',
                    size: 11 
                },
                footerFont: {
                    family: 'Graphik-Light, sans-serif',
                    size: 10 
                }
            },
        },

        layout: {
            padding: {
                top: 0,
                right: 20,
                bottom: 20,
                left: 12,
            }
        }
    };

    return (
        <div className="App h-w-100">
            <Bar data={data} options={options} plugins={[dottedLinePlugin]} />
        </div>
    );
}

